import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Filter, X, DollarSign, TrendingUp, TrendingDown, Clock, FileText,
  Loader2, Search, BarChart3, ArrowUpRight, ArrowDownLeft, Lock, CheckCircle2, XCircle, AlertCircle,
  Trash2, RotateCcw, Globe, RefreshCw, Info, ShieldAlert,
} from 'lucide-react';
import AdminPaymentEvents from './AdminPaymentEvents';
import RechargeReviewsPanel from './admin/RechargeReviewsPanel';
import Pagination, { PAGE_SIZE } from './Pagination';
import CustomerProfileDrawer from './CustomerProfileDrawer';
import PageHeader from './ui-shared/PageHeader';
import FilterPanel from './ui-shared/FilterPanel';
import DateRangeFilter from './ui-shared/DateRangeFilter';
import ExportMenu from './ui-shared/ExportMenu';
import SortableHeader, { useTableSort } from './SortableHeader';
import useAdminLoginFeed from '../hooks/useAdminLoginFeed';
import { useUnreadBadges } from '../context/UnreadBadgeContext';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
  LineChart, Line,
} from 'recharts';

const API_URL_TX = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminTransactions wrapper — adds a sub-tab strip so "PayPal Events" is no
 * longer a separate top-level menu item. Reduces clutter in the main nav
 * and keeps related billing screens together.
 */
export default function AdminTransactionsWrapper({ token, onOpenOrder, initialFilters = null, initialTab = '' }) {
  const [tab, setTab] = useState(initialTab || 'transactions');
  const [paypalUnverified, setPaypalUnverified] = useState(0);
  const [pendingReviews, setPendingReviews] = useState(0);

  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

  const loadReviewsBadge = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL_TX}/api/admin/risk/recharge-reviews/pending-count`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      setPendingReviews(d.pending || 0);
    } catch (_) { /* silent */ }
  }, [token]);
  useEffect(() => {
    loadReviewsBadge();
    const id = setInterval(loadReviewsBadge, 120000);
    return () => { clearInterval(id); };
  }, [loadReviewsBadge]);

  // Pull the unverified-events count for the badge on the sub-tab.
  //
  // We only count events that are still failed AND not acknowledged —
  // admin can dismiss stale sandbox / test events from the log without
  // wiping the audit row, and the badge should reflect that.
  const loadPaypalBadge = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL_TX}/api/paypal/webhook-events?verified=false&acknowledged=false&limit=1`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      setPaypalUnverified(d.total || 0);
    } catch (_) { /* silent */ }
  }, [token]);
  useEffect(() => {
    loadPaypalBadge();
    const id = setInterval(loadPaypalBadge, 120000);
    return () => { clearInterval(id); };
  }, [loadPaypalBadge]);

  return (
    <div data-testid="admin-transactions-wrapper">
      <div className="px-4 md:px-6 pt-4 md:pt-6 w-full lg:w-4/5 mx-auto">
        <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-1" data-testid="tx-subtabs">
          <button
            data-testid="tx-subtab-transactions"
            onClick={() => setTab('transactions')}
            className={`px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2 transition-all ${
              tab === 'transactions' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <DollarSign className="w-4 h-4" /> Transactions
          </button>
          <button
            data-testid="tx-subtab-paypal-events"
            onClick={() => setTab('paypal-events')}
            className={`relative px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2 transition-all ${
              tab === 'paypal-events' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Globe className="w-4 h-4" /> PayPal Events
            {paypalUnverified > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-rose-500 text-white">{paypalUnverified}</span>
            )}
          </button>
          <button
            data-testid="tx-subtab-reviews"
            onClick={() => setTab('reviews')}
            className={`relative px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2 transition-all ${
              tab === 'reviews' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <ShieldAlert className="w-4 h-4" /> On review
            {pendingReviews > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-amber-500 text-white">{pendingReviews}</span>
            )}
          </button>
        </div>
      </div>
      {tab === 'transactions'   && <AdminTransactionsBody token={token} onOpenOrder={onOpenOrder} initialFilters={initialFilters} />}
      {tab === 'paypal-events'  && <AdminPaymentEvents token={token} onBadgeShouldRefresh={loadPaypalBadge} />}
      {tab === 'reviews'        && <RechargeReviewsPanel token={token} onDecided={loadReviewsBadge} />}
    </div>
  );
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve a human-readable customer label from a transaction or order row.
// Priority: first_name + surname → company_name → email. Used in the
// Customer column of every admin table that previously rendered email.
function customerLabel(row) {
  const name = [row?.customer_first_name, row?.customer_surname].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (row?.customer_company_name) return row.customer_company_name;
  return row?.user_email || row?.email || '—';
}

// Decode HTML entities and pull a payment reference out of legacy
// description strings. The old PHP admin stored every external txn id
// inside a freeform sentence (e.g. "paypal txn id 4BD4…", "stripe
// payment, id pi_1…", "Decta id ddef…", "paymentwall ref id 18101").
// We never preserved a structured field on import, so the table cell
// has to recognise the patterns at render time.
const PAY_REF_PATTERNS = [
  // matcher, kind, label. Real CSV rows include `new method | paypal …`
  // and `paypal txn id08M…` (no space between "id" and the ref), so we
  // strip a leading `new method |` prefix first and tolerate optional
  // whitespace between "id" and the ref.
  [/paypal\s+txn\s*id\s*(\S+)/i,                       'paypal',      'PayPal'],
  [/stripe\s+(?:txn\s*id|payment,\s*id)\s*(\S+)/i,      'stripe',     'Stripe'],
  [/Decta\s+id\s*(\S+)/i,                              'decta',       'Decta'],
  [/paymentwall\s+ref\s+id\s*(\S+)/i,                  'paymentwall', 'Paymentwall'],
  [/Interkassa\s+ref\s+id\s*(\S*)/i,                   'interkassa',  'Interkassa'],
];
function decodeEntities(s) {
  if (!s) return '';
  return String(s)
    .replace(/&#(\d+);/g, (_, c) => String.fromCharCode(Number(c)))
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#039;|&apos;/g, "'");
}
function parsePayRef(desc) {
  let text = decodeEntities(desc || '').trim();
  if (!text) return null;
  // The legacy admin sometimes appended a method-switcher prefix like
  // "new method | paypal txn id …". Strip it so the regex below matches
  // the actual payment string.
  text = text.replace(/^new\s+method\s*\|\s*/i, '');
  for (const [re, kind, label] of PAY_REF_PATTERNS) {
    const m = text.match(re);
    if (m) return { kind, label, ref: m[1] || null, raw: text };
  }
  if (/bonus\s+balance\s+time\s+has\s+expired/i.test(text)) {
    return { kind: 'bonus_expired', label: 'Bonus expired', ref: null, raw: text };
  }
  if (/welcome\s+bonus/i.test(text)) {
    return { kind: 'bonus', label: 'Welcome bonus', ref: null, raw: text };
  }
  return null;
}
const PM_CHIP_TONE = {
  paypal:        'bg-blue-100 text-blue-700',
  stripe:        'bg-violet-100 text-violet-700',
  decta:         'bg-cyan-100 text-cyan-700',
  paymentwall:   'bg-emerald-100 text-emerald-700',
  interkassa:    'bg-pink-100 text-pink-700',
  admin_custom:  'bg-amber-100 text-amber-800',
  bonus:         'bg-amber-100 text-amber-800',
  bonus_expired: 'bg-slate-200 text-slate-700',
};

// Inline debug popover — operators sometimes need to see the raw fields
// behind the rendered description (legacy CSV often had garbled / null
// pieces). Click the (i) icon to toggle a small overlay showing the raw
// description, parsed payment ref, linked order summary, payment method
// and legacy_metadata.
function DescriptionInspectButton({ t }) {
  const [open, setOpen] = React.useState(false);
  const parsed = parsePayRef(t?.description || '');
  const los = t?.linked_order_summary || null;
  const meta = t?.legacy_metadata || null;
  const display = (() => {
    if (t?.legacy_source === 'legacy_balance_adjustment') {
      return `Balance reconciled (${t?.direction === 'to_user' ? '+' : '-'}€${Number(t?.amount || 0).toFixed(2)})`;
    }
    if (t?.legacy_source === 'old_php_admin' && t?.direction === 'from_user' && los) {
      return `legacy debit · № ${los.order_number || los.legacy_order_id || ''} · ${[los.manufacturer, los.model, los.engine].filter(Boolean).join(' · ')}${los.ecu_type ? ' · ' + los.ecu_type : ''}`;
    }
    if (parsed) {
      return `${parsed.label}${parsed.ref ? ' · ' + parsed.ref : ''}`;
    }
    return decodeEntities(t?.description || '');
  })();
  return (
    <span className="relative inline-block flex-shrink-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title="Inspect raw transaction fields"
        data-testid={`tx-inspect-${t.id}`}
        className="p-0.5 text-slate-400 hover:text-cyan-600 rounded"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="fixed inset-0 z-40 cursor-default"
            aria-label="close-inspect"
          />
          <div
            className="absolute right-0 top-5 z-50 w-[340px] bg-white border border-slate-300 rounded-lg shadow-xl p-3 text-[11px] text-slate-700 space-y-1"
            data-testid={`tx-inspect-panel-${t.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-bold text-slate-800 text-xs border-b border-slate-200 pb-1 mb-1">
              Inspect transaction
            </div>
            <div><span className="text-slate-500">display:</span> <span className="font-medium">{display}</span></div>
            <div><span className="text-slate-500">raw description:</span> <code className="bg-slate-50 px-1 rounded break-all">{decodeEntities(t?.description) || '(empty)'}</code></div>
            <div><span className="text-slate-500">pay_method:</span> {t?.payment_method || '—'}</div>
            <div><span className="text-slate-500">parsed ref:</span> {parsed ? <code className="bg-slate-50 px-1 rounded">{parsed.kind} → {parsed.ref || '(no ref)'}</code> : '—'}</div>
            <div><span className="text-slate-500">order_id:</span> <code className="bg-slate-50 px-1 rounded break-all">{t?.order_id || '—'}</code></div>
            <div><span className="text-slate-500">order_number:</span> {t?.order_number || '—'}</div>
            <div><span className="text-slate-500">linked_order_summary:</span>
              {los ? (
                <pre className="bg-slate-50 px-2 py-1 rounded text-[10px] overflow-x-auto">{JSON.stringify(los, null, 2)}</pre>
              ) : <span> —</span>}
            </div>
            <div><span className="text-slate-500">legacy_source:</span> {t?.legacy_source || '—'}</div>
            <div><span className="text-slate-500">legacy_transaction_id:</span> {t?.legacy_transaction_id || '—'}</div>
            {meta && Object.keys(meta).length > 0 && (
              <div><span className="text-slate-500">legacy_metadata:</span>
                <pre className="bg-slate-50 px-2 py-1 rounded text-[10px] overflow-x-auto">{JSON.stringify(meta, null, 2)}</pre>
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}

// Renders txn description with the order number turned into a clickable
// link that jumps to Admin → Orders → opens that order's detail drawer.
// Falls back to the raw description for non-order rows.
function DescriptionWithOrderLink({ t, onOpenOrder }) {
  const desc = decodeEntities(t?.description || '');
  const orderNum = t?.order_number;

  // ── Legacy balance reconciliation (synthetic top-ups we created to
  //    align imported balances). Shorten the verbose "setting current
  //    balance (X) to calculated (Y)" sentence so the column stays
  //    scannable. Direction tells us if it was a credit or debit.
  if (t?.legacy_source === 'legacy_balance_adjustment') {
    const credit = t?.direction === 'to_user';
    return (
      <span className="block leading-tight" title={desc}>
        <span className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-slate-200 text-slate-700">
          reconciled
        </span>
        <span className="text-slate-700">
          {credit ? 'Balance reconciled (+€' : 'Balance reconciled (−€'}
          {Number(t?.amount || 0).toFixed(2)})
        </span>
      </span>
    );
  }

  // ── Legacy debit (order payment from old PHP admin) ──
  if (t?.legacy_source === 'old_php_admin' && t?.direction === 'from_user') {
    const los = t?.linked_order_summary;
    if (los && (los.manufacturer || los.model || los.order_number)) {
      const onum = los.order_number || `#${los.legacy_order_id || los.order_id?.slice(0, 8) || ''}`;
      const vehicle = [los.manufacturer, los.model, los.engine].filter(Boolean).join(' · ');
      return (
        <span className="block leading-tight" title={desc}>
          <span className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-rose-100 text-rose-700">
            legacy debit
          </span>
          {onum && los.order_id && onOpenOrder ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenOrder(los.order_id); }}
              className="font-bold text-cyan-700 hover:text-cyan-900 hover:underline mr-1"
              data-testid={`tx-legacy-order-link-${t.id}`}
            >
              № {onum}
            </button>
          ) : onum ? (
            <span className="font-bold mr-1 text-slate-700">№ {onum}</span>
          ) : null}
          {vehicle && <span className="text-slate-700">{vehicle}</span>}
          {los.ecu_type && <span className="ml-1 text-slate-500">· {los.ecu_type}</span>}
        </span>
      );
    }
    return (
      <span className="block leading-tight" title={desc}>
        <span className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-rose-100 text-rose-700">
          legacy debit
        </span>
        <span
          className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-800"
          title="No matching order found via linked_transaction_id"
          data-testid={`tx-legacy-debit-orphan-${t.id}`}
        >
          ⚠ no linked order
        </span>
        <span className="line-clamp-2 text-slate-700">{desc || '(legacy order debit)'}</span>
      </span>
    );
  }

  // ── Welcome bonus + bonus rows (top-up direction, classifier-stamped) ──
  // We render these BEFORE the legacy top-up branch so a paypal bonus
  // doesn't get rendered as a plain PAYPAL recharge.
  if (t?.type === "welcome_bonus" || t?.payment_method_label === "WELCOME BONUS") {
    return (
      <span className="block leading-tight" title={desc}>
        <span className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-amber-200 text-amber-900"
              data-testid={`tx-welcome-bonus-${t.id}`}>
          WELCOME BONUS
        </span>
        <span className="text-slate-700 text-[12px]">{desc || 'Welcome bonus credit'}</span>
      </span>
    );
  }
  if (t?.type === "bonus" || t?.category === "bonus") {
    const label = t?.payment_method_label || "BONUS";
    const parsed = parsePayRef(desc);
    return (
      <span className="block leading-tight" title={desc}>
        <span className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-700"
              data-testid={`tx-bonus-${t.id}`}>
          {label}
        </span>
        {parsed?.ref && (
          <code className="text-[10px] text-slate-700 bg-slate-50 border border-slate-200 px-1 rounded mr-1">
            {parsed.ref}
          </code>
        )}
      </span>
    );
  }

  // ── Legacy top-up (direction=to_user, old_php_admin) — parse ref id
  //    out of the freeform description, render a coloured chip + ref.
  if (t?.legacy_source === 'old_php_admin' && t?.direction === 'to_user') {
    const parsed = parsePayRef(desc);
    const pm = (t?.payment_method || parsed?.kind || '').toLowerCase();
    const chipTone = PM_CHIP_TONE[pm] || 'bg-slate-200 text-slate-700';
    const labelMap = {
      paypal: 'PayPal', stripe: 'Stripe', decta: 'Decta',
      paymentwall: 'Paymentwall', interkassa: 'Interkassa',
      admin_custom: 'Admin', bonus: 'Bonus', bonus_expired: 'Bonus expired',
    };
    const label = labelMap[pm] || (t?.payment_method || 'Top-up');
    const ref = parsed?.ref || null;
    // For admin_custom or empty descriptions we fall back to the
    // description text or "Top-up" when nothing is available.
    const trailing = (() => {
      if (ref) return null; // ref already covers the info
      if (pm === 'admin_custom') return desc || 'admin top-up';
      if (pm === 'bonus' || pm === 'bonus_expired') return desc || 'bonus credit';
      return desc || 'top-up';
    })();
    return (
      <span className="block leading-tight" title={desc}>
        <span className={`inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase ${chipTone}`}
              data-testid={`tx-legacy-topup-chip-${t.id}`}>
          {label}
        </span>
        {ref ? (
          <code className="text-[10px] text-slate-700 bg-slate-50 border border-slate-200 px-1 rounded mr-1"
                data-testid={`tx-legacy-topup-ref-${t.id}`}>
            {ref}
          </code>
        ) : (
          // Chip without a ref looks unfinished — surface the absence
          // explicitly so the operator knows the CSV row simply did not
          // carry the external id (common for Decta rows imported from
          // the old admin).
          ['paypal', 'stripe', 'decta', 'paymentwall', 'interkassa'].includes(pm) && (
            <span className="text-[10px] text-slate-400 italic mr-1"
                  data-testid={`tx-legacy-topup-noref-${t.id}`}>
              (no ref)
            </span>
          )
        )}
        {trailing && <span className="text-slate-600 text-[12px]">{trailing}</span>}
      </span>
    );
  }

  if (!orderNum || !onOpenOrder || !t?.order_id) {
    return <span className="line-clamp-1" title={desc}>{desc}</span>;
  }
  // Render the order number as a clickable chip. If the description
  // already contains the number (legacy rows from the old PHP admin
  // baked it into the description), splice the chip in place. If not
  // (modern rows just say "new order" / "refunded"), tack the chip
  // onto the end so the operator can always click through to the
  // order — that was the original product expectation.
  const containsNumber = desc.includes(orderNum);
  const orderChip = (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpenOrder(t.order_id); }}
      className="font-bold text-cyan-700 hover:text-cyan-900 hover:underline"
      data-testid={`tx-desc-order-link-${t.id}`}
    >
      № {orderNum}
    </button>
  );
  if (!containsNumber) {
    return (
      <span className="line-clamp-1" title={desc}>
        {desc && <span>{desc} · </span>}
        {orderChip}
      </span>
    );
  }
  const parts = desc.split(orderNum);
  return (
    <span className="line-clamp-1" title={desc}>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 && orderChip}
        </React.Fragment>
      ))}
    </span>
  );
}

const STATUS_META = {
  pending:   { label: 'Pending',   cls: 'bg-slate-100 text-slate-700 border-slate-300', icon: Clock },
  tested:    { label: 'Locked',    cls: 'bg-amber-100 text-amber-700 border-amber-300', icon: Lock },
  locked:    { label: 'Locked',    cls: 'bg-amber-100 text-amber-700 border-amber-300', icon: Lock },
  end:       { label: 'End',       cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
  failed:    { label: 'Failed',    cls: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle },
  start:     { label: 'Start',     cls: 'bg-blue-100 text-blue-700 border-blue-300', icon: Clock },
};

const DIR_META = {
  to_user:   { label: 'To user',   cls: 'text-emerald-600', icon: ArrowDownLeft },
  from_user: { label: 'From user', cls: 'text-rose-600',    icon: ArrowUpRight },
};

// Map company role / id → tiny issuer chip with the country code.
// Stripe always has company_id (UK), PayPal modern captures store
// company_id, legacy PayPal stores only routing.company_role — both
// paths are handled. Plain text codes only — flag emojis render as
// "US"/"GB"/"LT" letters on systems without emoji-flag fonts and would
// duplicate the code (US USA / LT LT).
const ISSUER_BY_ROLE = {
  UK_COMPANY:  { code: 'UK',  tone: 'bg-blue-50 text-blue-800 border-blue-200' },
  USA_COMPANY: { code: 'USA', tone: 'bg-rose-50 text-rose-800 border-rose-200' },
  EU_COMPANY:  { code: 'LT',  tone: 'bg-amber-50 text-amber-800 border-amber-200' },
};

function IssuerChip({ tx, companiesById }) {
  const role = (tx?.routing && tx.routing.company_role)
    || (tx?.company_id && companiesById?.[tx.company_id]?.role)
    || null;
  const meta = role ? ISSUER_BY_ROLE[role] : null;
  if (!meta) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-bold ${meta.tone}`}
      title={`Issuer: ${role}`}
      data-testid={`tx-issuer-${tx.id}`}
    >
      {meta.code}
    </span>
  );
}

// Last-invoice-email chip + on-demand resend.
// `tx.invoice_email_status` = "sent" | "failed" | undefined.
// Only meaningful for recharge / invoice-bearing transactions.
function InvoiceEmailCell({ tx, token, onResent }) {
  const [busy, setBusy] = React.useState(false);
  const isInvoiceCarrier = (tx.type === 'recharge' || tx.invoice_number) && !tx.is_bonus;
  if (!isInvoiceCarrier) {
    return <span className="text-slate-300 text-xs">—</span>;
  }
  const status = tx.invoice_email_status; // sent | failed | undefined
  const sentAt = tx.invoice_email_sent_at ? String(tx.invoice_email_sent_at).slice(0, 16).replace('T', ' ') : null;

  const handleResend = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/transactions/${tx.id}/resend-invoice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      if (onResent) onResent(tx.id, j.sent);
    } catch (err) {
      // Surface the failure inline; sonner is already imported app-wide.
      // eslint-disable-next-line no-alert
      alert(`Resend failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  let chip;
  if (status === 'sent') {
    chip = (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold bg-emerald-50 text-emerald-700 border-emerald-200"
        title={`Sent ${sentAt || ''}${tx.invoice_email_to ? ' to ' + tx.invoice_email_to : ''}`}
        data-testid={`tx-invoice-email-${tx.id}`}
      >
        <CheckCircle2 className="w-3 h-3" /> Sent
      </span>
    );
  } else if (status === 'failed') {
    chip = (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold bg-rose-50 text-rose-700 border-rose-200"
        title={tx.invoice_email_error ? `Failed: ${tx.invoice_email_error}` : 'Failed to deliver'}
        data-testid={`tx-invoice-email-${tx.id}`}
      >
        <XCircle className="w-3 h-3" /> Failed
      </span>
    );
  } else {
    chip = (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold bg-slate-100 text-slate-600 border-slate-200"
        title="No invoice email recorded yet — likely a legacy transaction or the auto-send was skipped."
        data-testid={`tx-invoice-email-${tx.id}`}
      >
        — None
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {chip}
      {status !== 'sent' && (
        <button
          onClick={handleResend}
          disabled={busy}
          title={status === 'failed' ? 'Retry sending the invoice email' : 'Send invoice email now'}
          className="text-[10px] px-1.5 py-0.5 rounded border border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 disabled:opacity-50"
          data-testid={`tx-invoice-resend-${tx.id}`}
        >
          {busy ? '…' : 'Send'}
        </button>
      )}
    </div>
  );
}


const PIE_COLORS = ['#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const fmtDate = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' ? new Date(v) : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const blank = {
  user_email: '', account_name: '', company: '', country: '', description: '',
  date_start: '', date_end: '', direction: '', status: '', type: '',
  money_op: '', money_value: '', money_value2: '',
  user_id: '', payment_company: '',
  bonus_filter: 'all', // 'all' | 'hide' | 'only'
  // Security Center lens (no_payment_id | big | bonus | reversal | admin_manual).
  // Must live in `blank` so a deep-link from Security survives the
  // `k in blank` whitelist and reaches /api/transactions/filter.
  suspicious: '',
};

// High-level category chips above the table. Default `all_real` hides the
// noisy reconciliation rows (synthetic admin_adjustments we created at
// legacy import time) — they were burying real business transactions in
// the default view.
const CATEGORY_CHIPS = [
  { id: 'all_real',       label: 'All real',         tone: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { id: 'top_ups',        label: 'Top-ups',          tone: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { id: 'bonuses',        label: 'Bonuses',          tone: 'bg-amber-100 text-amber-700 border-amber-300' },
  { id: 'order_debits',   label: 'Order debits',     tone: 'bg-rose-100 text-rose-700 border-rose-300' },
  { id: 'admin_custom',   label: 'Admin custom',     tone: 'bg-amber-100 text-amber-800 border-amber-300' },
  { id: 'legacy',         label: 'Legacy',           tone: 'bg-violet-100 text-violet-700 border-violet-300' },
  { id: 'reconciliation', label: 'Reconciliation',   tone: 'bg-slate-200 text-slate-700 border-slate-300' },
];

function AdminTransactionsBody({ token, onOpenOrder, initialFilters = null }) {
  // Deep-link from Analytics — seeded at mount so the first fetch already
  // carries the filter (no unfiltered request to race with).
  const [filters, setFilters] = useState(initialFilters
    ? { ...blank, ...Object.fromEntries(Object.entries(initialFilters).filter(([k]) => k in blank)) }
    : blank);
  // High-level category — drives the bucket chips above the table.
  // Default 'all_real' hides legacy_balance_adjustment reconciliation
  // rows so genuine top-ups and order debits surface immediately.
  const [category, setCategory] = useState(initialFilters?.category || 'all_real');
  // Per-category row counts for the chip badges. Refreshed each load.
  const [categoryCounts, setCategoryCounts] = useState({});
  const [tx, setTx] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [page, setPage] = useState(1);
  // Server-side sort — clicking a sortable header passes sort_by/sort_dir
  // to /api/transactions/filter. Resets to page 1 on every change so the
  // operator always sees the top of the new order.
  const { sortBy, sortDir, onSortChange, sortParams } = useTableSort({
    onPageReset: () => setPage(1),
  });
  const [openCustomerId, setOpenCustomerId] = useState(null);
  // Admin-wide dropdown options for Country & Account selects — fetched once.
  const [pickerOptions, setPickerOptions] = useState({ countries: [], accounts: [], payment_companies: [] });
  // id → company doc — used by IssuerChip to map company_id → role/flag
  // for transactions that lack `routing.company_role` (Stripe + new
  // PayPal captures).
  const [companiesById, setCompaniesById] = useState({});
  // Granular per-row "NEW" tracking — clicking a transaction marks it
  // seen and decrements the menu badge by exactly 1.
  const { markTransactionSeen, unreadTransactionIds } = useUnreadBadges();

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Deep-link from Analytics — reset to a clean slate, then apply the
  // incoming subset (date range, type, description, user_email, category).
  const deepLinkPendingRef = React.useRef(false);
  useEffect(() => {
    if (!initialFilters) return;
    const { _nonce, category: cat, ...incoming } = initialFilters;
    setFilters({ ...blank, ...Object.fromEntries(
      Object.entries(incoming).filter(([k]) => k in blank),
    ) });
    setCategory(cat || 'all_real');
    setShowFilters(true);
    setPage(1);
    deepLinkPendingRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters?._nonce]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/filters/options`, { headers });
        if (r.ok) {
          const d = await r.json();
          setPickerOptions({ countries: d.countries || [], accounts: d.accounts || [], payment_companies: d.payment_companies || [] });
        }
      } catch (_) { /* silent */ }
      try {
        // Public companies feed — small, cached. Used to enrich txn rows
        // with the issuer flag/code chip.
        const r = await fetch(`${API_URL}/api/companies/`, { headers });
        if (r.ok) {
          const arr = await r.json();
          const map = {};
          (arr || []).forEach((c) => { if (c.id) map[c.id] = c; });
          setCompaniesById(map);
        }
      } catch (_) { /* silent */ }
    })();
  }, [headers]);

  const buildQs = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    const merged = { ...filters, category, ...extra };
    // Translate UI bonus_filter into backend hide_bonus / only_bonus
    if (merged.bonus_filter === 'hide') { merged.hide_bonus = true; }
    if (merged.bonus_filter === 'only') { merged.only_bonus = true; }
    delete merged.bonus_filter;
    Object.entries(merged).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined && v !== false) params.append(k, v);
    });
    return params.toString();
  }, [filters, category]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const qs = buildQs({ limit: PAGE_SIZE, skip });
      const url = `${API_URL}/api/transactions/filter?${qs}${sortParams()}`;
      const r = await fetch(url, { headers });
      const data = r.ok ? await r.json() : [];
      setTx(Array.isArray(data) ? data : []);
      // Analytics is over the full filtered set, not just the current page
      const aQs = buildQs();
      const a = await fetch(`${API_URL}/api/transactions/analytics?${aQs}`, { headers });
      if (a.ok) setAnalytics(await a.json());
    } finally { setLoading(false); }
  }, [buildQs, headers, page, sortParams]);

  // Per-category counts — runs analytics for each chip in parallel so the
  // chip badges stay accurate. Respects all the other (non-category)
  // filters so e.g. typing 'txn' updates the chip numbers too.
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    const merged = { ...filters };
    if (merged.bonus_filter === 'hide') { merged.hide_bonus = true; }
    if (merged.bonus_filter === 'only') { merged.only_bonus = true; }
    delete merged.bonus_filter;
    Object.entries(merged).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined && v !== false) params.append(k, v);
    });
    fetch(`${API_URL}/api/transactions/category-counts?${params.toString()}`, { headers })
      .then((r) => (r.ok ? r.json() : {}))
      .then((counts) => { if (!cancelled) setCategoryCounts(counts || {}); })
      .catch(() => { /* silent — chip badges are best-effort */ });
    return () => { cancelled = true; };
  }, [filters, headers]);

  // Intentionally omit `load` from deps — it changes whenever filters
  // change, but we only want the table to refresh when token or page
  // changes. Filter-driven refreshes go through scheduleReload below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token, page, category]);

  // Deep-link filters need an explicit refetch — the table otherwise only
  // reloads on Apply / page / category change.
  useEffect(() => {
    if (!deepLinkPendingRef.current) return;
    deepLinkPendingRef.current = false;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Live refresh — when a new order, recharge or admin top-up lands, the
  // transactions table should reflect it without a manual reload. We debounce
  // the socket-driven reload (fired up to ~once/second) so a flurry of
  // events doesn't hammer the API.
  const debouncedReloadRef = React.useRef(null);
  const scheduleReload = useCallback(() => {
    if (debouncedReloadRef.current) return;
    debouncedReloadRef.current = setTimeout(() => {
      debouncedReloadRef.current = null;
      load();
    }, 1000);
  }, [load]);
  useEffect(() => () => {
    if (debouncedReloadRef.current) clearTimeout(debouncedReloadRef.current);
  }, []);
  useAdminLoginFeed({
    token,
    enabled: true,
    onOrder: scheduleReload,
    onRecharge: scheduleReload,
  });

  const apply = () => { setPage(1); load(); };
  const clear = () => { setFilters(blank); setCategory('all_real'); setPage(1); setTimeout(load, 50); };

  const downloadInvoice = async (t) => {
    try {
      const r = await fetch(`${API_URL}/api/transactions/${t.id}/invoice`, { headers });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url; a.download = `invoice-${t.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const deleteAdjustment = async (t) => {
    // Two shapes are deletable (see backend contract):
    //   - manual credit/debit rows (`manual_kind`),
    //   - auto-generated bonus companion rows (`is_bonus`).
    const label =
      t.manual_kind === 'credit' ? 'manual credit'
      : t.manual_kind === 'debit' ? 'manual debit'
      : t.is_bonus ? 'bonus row'
      : 'admin adjustment';
    if (!window.confirm(`Delete this ${label} of €${Number(t.amount || 0).toFixed(2)} for ${t.user_email}?\n\nThe customer's balance will be rolled back. This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/transactions/admin/${t.id}`, {
        method: 'DELETE', headers,
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      load();
    } catch (e) { alert('Delete failed: ' + e.message); }
  };

  return (
    <div className="p-4 md:p-6 w-full lg:w-4/5 mx-auto" data-testid="admin-transactions-page">
      {/* Custom 3-column header (08/Jun/2026) — replaces the shared
          `PageHeader` for this page so the KPI strip can live in the
          CENTER of the row instead of being glued to the right next
          to the Export button. Layout: title-left · stats-center · 
          buttons-right. Stats are ~50% wider than before per user
          request. */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="admin-transactions-header">
        {/* LEFT — title block */}
        <div className="flex items-start gap-3 min-w-0 flex-shrink-0">
          <div className="w-9 h-9 rounded-lg border bg-cyan-50 text-cyan-600 border-cyan-200 flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h1 className="text-[20px] sm:text-[22px] font-semibold text-slate-900 leading-tight truncate tracking-tight">Transactions</h1>
            <p className="text-[13px] text-slate-500 leading-snug mt-0.5">All payments, blocks, refunds and adjustments</p>
            <p className="text-[11px] text-slate-400 mt-1 leading-tight">Counters always reflect the active chip + filters</p>
          </div>
        </div>

        {/* CENTER — KPI mini-stats. ~50% wider than the previous
            right-aligned version (min-w-[140px] vs 88px) so the
            empty middle band is used. */}
        {analytics && (
          <div className="flex flex-wrap items-stretch justify-center gap-2 flex-1 min-w-0" data-testid="tx-mini-stats">
            {[
              { tone: 'bg-cyan-500',    label: 'Total',     value: analytics.total_count.toLocaleString(), hint: null, testid: 'stat-total' },
              { tone: 'bg-emerald-500', label: 'Paid (€)',  value: `€${(analytics.paid_total || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,  hint: `${analytics.paid_count || 0} txn`, testid: 'stat-paid-total' },
              { tone: 'bg-amber-500',   label: 'Bonus (€)', value: `€${(analytics.bonus_total || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, hint: `${analytics.bonus_count || 0} txn`, testid: 'stat-bonus-total' },
              { tone: 'bg-rose-500',    label: 'From user', value: `€${((analytics.by_direction.find((x) => x.key === 'from_user') || {}).amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, hint: null, testid: 'stat-from-user' },
              { tone: 'bg-violet-500',  label: 'To user',   value: `€${((analytics.by_direction.find((x) => x.key === 'to_user')   || {}).amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, hint: null, testid: 'stat-to-user' },
            ].map((s) => (
              <div
                key={s.testid}
                className="relative bg-white border border-slate-200 rounded-lg px-3 py-1.5 min-w-[140px] shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                data-testid={s.testid}
              >
                <span className={`absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full ${s.tone}`} aria-hidden />
                <p className="pl-2 text-[10px] uppercase font-semibold tracking-wide text-slate-500 leading-tight">{s.label}</p>
                <p className="pl-2 text-[14px] font-bold text-slate-900 tabular-nums leading-tight">{s.value}</p>
                {s.hint && <p className="pl-2 text-[10px] text-slate-400 leading-tight">{s.hint}</p>}
              </div>
            ))}
          </div>
        )}

        {/* RIGHT — action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => setShowCharts((s) => !s)} data-testid="toggle-charts-btn"
            className={`px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5 transition-all text-xs ${showCharts ? 'bg-cyan-100 text-cyan-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            <BarChart3 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Analytics</span>
          </button>
          <ExportMenu
            url={`${API_URL}/api/transactions/export?${buildQs()}`}
            token={token}
            filename="transactions"
            testid="admin-tx-export"
            label="Export"
          />
        </div>
      </div>

      {/* Category chips — high-level grouping above the table. Default
          'all_real' hides legacy reconciliation rows so genuine business
          transactions surface first. */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="tx-category-chips">
        {CATEGORY_CHIPS.map((c) => {
          const active = category === c.id;
          const count = categoryCounts[c.id];
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => { setCategory(c.id); setPage(1); }}
              data-testid={`tx-chip-${c.id}`}
              aria-pressed={active}
              className={`px-3 py-1.5 rounded-full border text-xs font-bold inline-flex items-center gap-2 transition-all ${
                active
                  ? `${c.tone} shadow-sm scale-105`
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {c.label}
              {typeof count === 'number' && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  active ? 'bg-white/60' : 'bg-slate-100 text-slate-500'
                }`}>
                  {count.toLocaleString()}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Charts */}
      {showCharts && analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3">By status</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={analytics.by_status.map((x) => ({ name: STATUS_META[x.key]?.label || x.key, value: x.amount }))} dataKey="value" outerRadius={70} label>
                  {analytics.by_status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `€${Number(v).toLocaleString()}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3">By direction</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={analytics.by_direction.map((x) => ({ name: DIR_META[x.key]?.label || x.key, count: x.count, amount: x.amount }))} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" />
                <YAxis width={80} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `€${Number(v).toLocaleString()}`} />
                <Bar dataKey="amount" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Daily activity</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={analytics.daily.slice(-30)} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis width={80} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `€${Number(v).toLocaleString()}`} />
                <Legend />
                <Line type="monotone" dataKey="in_amount"  stroke="#10B981" strokeWidth={2} name="To user" dot={false} />
                <Line type="monotone" dataKey="out_amount" stroke="#EF4444" strokeWidth={2} name="From user" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters — refactored to the shared FilterPanel pattern: Basic
          (search, date, type, direction, status) visible by default;
          advanced (country, account, payment company, customer company,
          balance type, amount) under a collapsible toggle. All filter
          semantics, `data-testid`s and state handlers are preserved. */}
      {(() => {
        const FILTER_LABELS = {
          date_start: 'Date from', date_end: 'Date to',
          account_name: 'Name', user_email: 'Email', country: 'Country',
          user_id: 'Account', payment_company: 'Seller co.',
          company: 'Company', description: 'Search',
          direction: 'Direction', status: 'Status', type: 'Type',
          bonus_filter: 'Balance type',
          suspicious: 'Security lens',
        };
        const dropdownLabel = (k, v) => {
          if (!v || v === 'all') return '';
          if (k === 'user_id') {
            const a = pickerOptions.accounts.find((x) => x.user_id === v);
            return a ? (a.label || a.email || v) : v;
          }
          if (k === 'payment_company') {
            const c = pickerOptions.payment_companies.find((x) => x.id === v);
            return c ? c.label : v;
          }
          return v;
        };
        const activeChips = Object.entries(filters)
          .filter(([k, v]) => v !== '' && v != null && k !== 'money_op' && k !== 'money_value' && k !== 'money_value2' && (k !== 'bonus_filter' || v !== 'all'))
          .map(([k, v]) => ({
            key: k,
            label: FILTER_LABELS[k] || k,
            value: dropdownLabel(k, v),
            onRemove: () => setFilters((prev) => ({ ...prev, [k]: k === 'bonus_filter' ? 'all' : '' })),
          }));
        // Amount filter chip (combined op + value(s))
        if (filters.money_op && filters.money_value !== '') {
          const opSym = { eq: '=', gt: '>', gte: '≥', lt: '<', lte: '≤', between: 'between' }[filters.money_op] || filters.money_op;
          const v = filters.money_op === 'between' && filters.money_value2 !== ''
            ? `${filters.money_value} – ${filters.money_value2}`
            : `${opSym} ${filters.money_value}`;
          activeChips.push({
            key: 'money', label: 'Amount', value: v,
            onRemove: () => setFilters((p) => ({ ...p, money_op: '', money_value: '', money_value2: '' })),
          });
        }
        const advancedKeys = ['country', 'user_id', 'payment_company', 'company', 'bonus_filter'];
        const advancedActiveCount = advancedKeys.filter((k) => filters[k] && (k !== 'bonus_filter' || filters[k] !== 'all')).length
          + (filters.money_op && filters.money_value !== '' ? 1 : 0);
        return (
          <FilterPanel
            open={showFilters}
            onToggle={() => setShowFilters((s) => !s)}
            activeChips={activeChips}
            onClearAll={clear}
            onApply={apply}
            advancedOpen={advancedOpen}
            onToggleAdvanced={() => setAdvancedOpen((s) => !s)}
            advancedActiveCount={advancedActiveCount}
            testid="adm-tx-filters"
            headerExtra={
              <button
                onClick={load}
                data-testid="refresh-tx-btn"
                disabled={loading}
                title="Reload transactions"
                className="h-7 px-2 rounded-md bg-cyan-50 text-cyan-700 hover:bg-cyan-100 disabled:opacity-50 flex items-center gap-1 text-[11px] font-medium"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            }
            advancedChildren={
              <>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Country</label>
                  <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} data-testid="tx-filter-country" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none">
                    <option value="">Choose country</option>
                    {pickerOptions.countries.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Account</label>
                  <select value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} data-testid="tx-filter-account" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none">
                    <option value="">Choose account</option>
                    {pickerOptions.accounts.map((a) => (<option key={a.user_id} value={a.user_id}>{a.label}{a.email ? ` · ${a.email}` : ''}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Seller company</label>
                  <select value={filters.payment_company} onChange={(e) => setFilters({ ...filters, payment_company: e.target.value })} data-testid="tx-filter-payment-company" title="Filter by seller company (UK/EU/USA) that receives this customer's payments." className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none">
                    <option value="">UK / EU / USA</option>
                    {pickerOptions.payment_companies.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Customer company</label>
                  <input type="text" value={filters.company} onChange={(e) => setFilters({ ...filters, company: e.target.value })} placeholder="Customer company name" data-testid="tx-filter-company" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Balance type</label>
                  <select value={filters.bonus_filter} onChange={(e) => setFilters({ ...filters, bonus_filter: e.target.value })} data-testid="tx-filter-bonus" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none">
                    <option value="all">All</option>
                    <option value="hide">Real payments only</option>
                    <option value="only">Bonus credits only</option>
                  </select>
                </div>
                {/* Amount filter — moved into Advanced per TZ. Operator + value (+ optional second value for "between"). */}
                <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide flex items-center gap-1"><DollarSign className="w-3 h-3" /> Amount</label>
                  <div className="flex flex-wrap gap-1.5">
                    <select value={filters.money_op} onChange={(e) => setFilters({ ...filters, money_op: e.target.value })} data-testid="tx-filter-money-op" className="h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                      <option value="">— No filter —</option>
                      <option value="eq">= equal to</option>
                      <option value="gt">&gt; greater than</option>
                      <option value="gte">≥ at least</option>
                      <option value="lt">&lt; less than</option>
                      <option value="lte">≤ at most</option>
                      <option value="between">between</option>
                    </select>
                    <input type="number" step="any" value={filters.money_value} onChange={(e) => setFilters({ ...filters, money_value: e.target.value })} placeholder="€ amount" data-testid="tx-filter-money-value" className="h-9 px-2 text-xs rounded-md border border-slate-200 w-28" />
                    {filters.money_op === 'between' && (
                      <input type="number" step="any" value={filters.money_value2} onChange={(e) => setFilters({ ...filters, money_value2: e.target.value })} placeholder="€ to" data-testid="tx-filter-money-value2" className="h-9 px-2 text-xs rounded-md border border-slate-200 w-28" />
                    )}
                  </div>
                </div>
              </>
            }
          >
            {/* BASIC FILTERS */}
            <div className="col-span-1 sm:col-span-2 lg:col-span-2">
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Search</label>
              <input type="text" value={filters.description} onChange={(e) => setFilters({ ...filters, description: e.target.value })} placeholder="Description, legacy id (88331), name or email" data-testid="tx-filter-description" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none" />
            </div>
            <DateRangeFilter
              from={filters.date_start}
              to={filters.date_end}
              onChange={({ from, to }) => setFilters({ ...filters, date_start: from, date_end: to })}
              testidPrefix="tx-filter-date"
            />
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Type</label>
              <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="tx-filter-type" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none">
                <option value="">Any type</option>
                <option value="recharge">Recharge</option>
                <option value="order_reserve">Order reserve</option>
                <option value="order_complete">Order complete</option>
                <option value="order_refund">Order refund</option>
                <option value="admin_adjustment">Admin adjustment</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Direction</label>
              <select value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })} data-testid="tx-filter-direction" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none">
                <option value="">Any direction</option>
                <option value="to_user">To user</option>
                <option value="from_user">From user</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Status</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="tx-filter-status" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none">
                <option value="">Any status</option>
                <option value="pending">Pending</option>
                <option value="tested">Locked</option>
                <option value="end">End</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Account name</label>
              <input type="text" value={filters.account_name} onChange={(e) => setFilters({ ...filters, account_name: e.target.value })} placeholder="First or surname" data-testid="tx-filter-account-name" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Email</label>
              <input type="text" value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })} placeholder="customer@example.com" data-testid="tx-filter-email" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none" />
            </div>
          </FilterPanel>
        );
      })()}


      {/* Table — desktop only */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-360px)]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-200 border-b border-slate-300">
              <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                <SortableHeader label="ID" field="legacy_id" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Date" field="date" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Account" field="user_email" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <th className="px-2.5 py-3 text-left whitespace-nowrap" data-testid="tx-th-account-name">Account name</th>
                <SortableHeader label="Type" field="type" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Direction" field="direction" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Amount" field="amount" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} align="right" className="hover:bg-slate-300/50" />
                <SortableHeader label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Country" field="country" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Issuer" field="issuer" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Invoice email" field="invoice_email" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Description" field="description" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <th className="px-2.5 py-3 text-center whitespace-nowrap">Invoice</th>
              </tr>
            </thead>
            <tbody data-testid="tx-tbody">
              {loading ? (
                <tr><td colSpan={13} className="text-center py-12 text-slate-600"><Loader2 className="w-5 h-5 inline mr-2 animate-spin" />Loading…</td></tr>
              ) : tx.length === 0 ? (
                <tr><td colSpan={13} className="text-center py-12 text-slate-500">No transactions match the filters.</td></tr>
              ) : (
                tx.map((t, idx) => {
                  const sm = STATUS_META[t.status] || STATUS_META.pending;
                  const dm = DIR_META[t.direction] || { label: t.direction, cls: '', icon: ArrowUpRight };
                  const SIcon = sm.icon; const DIcon = dm.icon;
                  const isNew = unreadTransactionIds && unreadTransactionIds.has(t.id);
                  return (
                    <tr
                      key={t.id}
                      onClick={() => isNew && markTransactionSeen(t.id)}
                      className={`border-b border-slate-100 hover:bg-cyan-50/60 transition-colors cursor-pointer ${isNew ? 'bg-amber-50/70 border-l-4 border-l-amber-400' : (t.is_bonus ? 'bg-amber-50/40' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50')}`}
                      data-testid={t.is_bonus ? `tx-row-bonus-${t.id}` : `tx-row-${t.id}`}
                    >
                      {/* Legacy / old-site transaction ID — first column so it
                          mirrors the old PHP admin's "number of id" column.
                          For native new-site transactions this is empty → em-dash. */}
                      <td className="px-2.5 py-2 text-xs font-mono text-slate-700 whitespace-nowrap"
                          data-testid={`tx-legacy-id-${t.id}`}
                          title={t.legacy_transaction_id ? `Legacy old-site transaction id ${t.legacy_transaction_id}` : 'Native transaction (no legacy id)'}>
                        {t.legacy_transaction_id || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2.5 py-2 text-xs whitespace-nowrap font-mono">
                        {fmtDate(t.created_at)}
                        {isNew && (
                          <span className="ml-2 inline-flex items-center justify-center px-1.5 h-[16px] bg-amber-500 text-white text-[9px] font-extrabold rounded uppercase tracking-wider align-middle" data-testid={`admin-tx-new-${t.id}`}>NEW</span>
                        )}
                      </td>
                      <td className="px-2.5 py-2 text-xs max-w-[180px]">
                        {t.user_id ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setOpenCustomerId(t.user_id); }}
                            data-testid={`tx-customer-${t.id}`}
                            title={t.user_email}
                            className="font-medium text-cyan-700 hover:text-cyan-900 hover:underline text-left block truncate w-full"
                          >{t.user_email || '—'}</button>
                        ) : (
                          <div className="font-medium text-slate-800 truncate" title={t.user_email}>{t.user_email || '—'}</div>
                        )}
                      </td>
                      {/* Account name — first + surname, populated on
                          transaction write from the customer's profile.
                          Legacy rows or rows created before this field
                          existed render em-dash. */}
                      <td className="px-2.5 py-2 text-xs max-w-[160px]" data-testid={`tx-account-name-${t.id}`}>
                        {(() => {
                          const name = [t.customer_first_name, t.customer_surname].filter(Boolean).join(' ').trim();
                          if (!name) return <span className="text-slate-300">—</span>;
                          return <span className="font-medium text-slate-700 truncate block" title={name}>{name}</span>;
                        })()}
                      </td>
                      <td className="px-2.5 py-2 text-xs whitespace-nowrap">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-medium">{t.type}</span>
                        {t.is_bonus && (
                          <span className="ml-1 px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-[10px] font-bold uppercase tracking-wide" title="Bonus credit — no invoice">BONUS</span>
                        )}
                        {t.legacy_source === 'old_php_admin' && (
                          <span
                            className="ml-1 px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-[10px] font-bold uppercase tracking-wide"
                            title={`Legacy imported · legacy_transaction_id ${t.legacy_transaction_id || '—'}`}
                            data-testid={`txn-legacy-badge-${t.id}`}
                          >
                            legacy
                          </span>
                        )}
                      </td>
                      <td className={`px-2.5 py-2 text-xs whitespace-nowrap ${dm.cls}`}><div className="inline-flex items-center gap-1 font-medium"><DIcon className="w-3.5 h-3.5" />{dm.label}</div></td>
                      <td className={`px-2.5 py-2 text-right font-bold whitespace-nowrap tabular-nums ${t.direction === 'to_user' ? (t.is_bonus ? 'text-amber-600' : 'text-emerald-600') : 'text-rose-600'}`}>{t.direction === 'to_user' ? '+' : '−'}€{Number(t.amount || 0).toFixed(2)}</td>
                      <td className="px-2.5 py-2 whitespace-nowrap"><span className={`px-2 py-1 rounded border text-[10px] font-bold inline-flex items-center gap-1 ${sm.cls}`}><SIcon className="w-3 h-3" />{sm.label}</span></td>
                      <td className="px-2.5 py-2 text-xs text-slate-600 max-w-[120px] truncate" title={t.country || ''}>{t.country || '—'}</td>
                      <td className="px-2.5 py-2 whitespace-nowrap"><IssuerChip tx={t} companiesById={companiesById} /></td>
                      <td className="px-2.5 py-2 max-w-[200px]">
                        <InvoiceEmailCell
                          tx={t}
                          token={token}
                          onResent={(id, sent) => {
                            // Optimistic update — flip the chip without
                            // refetching the whole page. The backend
                            // already stamped the txn doc.
                            setTx((prev) => prev.map((x) => x.id === id
                              ? { ...x, invoice_email_status: sent ? 'sent' : 'failed', invoice_email_sent_at: sent ? new Date().toISOString() : x.invoice_email_sent_at }
                              : x));
                          }}
                        />
                      </td>
                      <td className="px-2.5 py-2 text-xs text-slate-600 max-w-[260px]">
                        <div className="flex items-start gap-1">
                          <div className="min-w-0 flex-1">
                            <DescriptionWithOrderLink t={t} onOpenOrder={onOpenOrder} />
                          </div>
                          <DescriptionInspectButton t={t} />
                        </div>
                        {/* PayPal payment status badge (only for PayPal-backed transactions) */}
                        {t.paypal_order_id && t.payment_status && (
                          <span className={`mt-0.5 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                            t.payment_status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700' :
                            t.payment_status === 'REFUND' || t.payment_status === 'REVERSAL' ? 'bg-amber-100 text-amber-800' :
                            t.payment_status === 'DISPUTED' ? 'bg-rose-200 text-rose-900' :
                            t.payment_status === 'FAILED' ? 'bg-red-100 text-red-700' :
                            t.payment_status === 'PENDING' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }`} data-testid={`tx-payment-status-${t.id}`}>
                            PP: {t.payment_status}
                          </span>
                        )}
                      </td>
                      <td className="px-2.5 py-2 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          {((t.type === 'recharge' && !t.is_bonus) || (t.type === 'admin_adjustment' && t.invoice_number)) && (
                            <button onClick={(e) => { e.stopPropagation(); markTransactionSeen(t.id); downloadInvoice(t); }} title="Download invoice" data-testid={`admin-invoice-${t.id}`}
                              className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded inline-flex items-center justify-center"><FileText className="w-4 h-4" /></button>
                          )}
                          {/* PayPal sync — visible only for transactions backed by a real PayPal order */}
                          {t.paypal_order_id && (
                            <span onClick={(e) => e.stopPropagation()}>
                              <SyncPaypalButton txId={t.id} orderId={t.paypal_order_id}
                                currentStatus={t.payment_status} token={token} onDone={load} />
                            </span>
                          )}
                          {t.type === 'admin_adjustment' && (t.manual_kind === 'credit' || t.manual_kind === 'debit' || !!t.is_bonus) && (
                            <button onClick={(e) => { e.stopPropagation(); deleteAdjustment(t); }} title="Delete adjustment & roll back balance" data-testid={`admin-tx-delete-${t.id}`}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded inline-flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                          )}
                          {!((t.type === 'recharge' && !t.is_bonus) || (t.type === 'admin_adjustment' && (t.invoice_number || t.manual_kind)) || t.paypal_order_id) && (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {analytics && analytics.total_count > 0 && (
          <Pagination page={page} total={analytics.total_count} pageSize={PAGE_SIZE} onChange={setPage} label="transactions" isAdmin />
        )}
      </div>

      {/* Mobile card stack — admin transactions */}
      <div className="md:hidden space-y-3" data-testid="admin-tx-mobile-cards">
        {loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Loading…
          </div>
        )}
        {!loading && tx.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            No transactions match the filters.
          </div>
        )}
        {!loading && tx.map((t) => {
          const sm = STATUS_META[t.status] || STATUS_META.pending;
          const dm = DIR_META[t.direction] || { label: t.direction, cls: '', icon: ArrowUpRight };
          const SIcon = sm.icon; const DIcon = dm.icon;
          const canInvoice = (t.type === 'recharge' && !t.is_bonus) || (t.type === 'admin_adjustment' && t.invoice_number);
          const isNew = unreadTransactionIds && unreadTransactionIds.has(t.id);
          return (
            <div
              key={t.id}
              data-testid={`tx-card-${t.id}`}
              onClick={() => isNew && markTransactionSeen(t.id)}
              className={`bg-white rounded-xl border ${isNew ? 'border-amber-400' : (t.is_bonus ? 'border-amber-300' : 'border-slate-200')} shadow-sm overflow-hidden ${isNew ? 'cursor-pointer' : ''}`}
            >
              <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                    {fmtDate(t.created_at)}
                    {isNew && (
                      <span className="ml-1 inline-flex items-center justify-center px-1.5 h-[14px] bg-amber-500 text-white text-[9px] font-extrabold rounded uppercase tracking-wider align-middle" data-testid={`admin-tx-new-mob-${t.id}`}>NEW</span>
                    )}
                  </p>
                  {t.user_id ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setOpenCustomerId(t.user_id); }}
                      data-testid={`tx-customer-mob-${t.id}`}
                      title={t.user_email}
                      className="font-semibold text-cyan-700 hover:underline text-sm break-all text-left"
                    >{customerLabel(t)}</button>
                  ) : (
                    <p className="font-semibold text-slate-800 text-sm break-all" title={t.user_email}>{customerLabel(t)}</p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-base font-bold ${t.direction === 'to_user' ? (t.is_bonus ? 'text-amber-600' : 'text-emerald-600') : 'text-rose-600'}`}>
                    {t.direction === 'to_user' ? '+' : '−'}€{Number(t.amount || 0).toFixed(2)}
                  </p>
                </div>
              </div>
              <dl className="px-4 py-2.5 text-sm divide-y divide-slate-100">
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Type</dt>
                  <dd className="flex items-center gap-1 flex-wrap justify-end">
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-medium">{t.type}</span>
                    {t.is_bonus && <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-[10px] font-bold uppercase tracking-wide">BONUS</span>}
                  </dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Direction</dt>
                  <dd className={`inline-flex items-center gap-1 font-medium ${dm.cls}`}>
                    <DIcon className="w-3.5 h-3.5" /> {dm.label}
                  </dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Status</dt>
                  <dd>
                    <span className={`px-2 py-1 rounded border text-[10px] font-bold inline-flex items-center gap-1 ${sm.cls}`}>
                      <SIcon className="w-3 h-3" />{sm.label}
                    </span>
                  </dd>
                </div>
                {t.country && (
                  <div className="flex justify-between py-1.5 gap-3 items-center">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Country</dt>
                    <dd className="text-slate-700">{t.country}</dd>
                  </div>
                )}
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Issuer</dt>
                  <dd><IssuerChip tx={t} companiesById={companiesById} /></dd>
                </div>
                {t.description && (
                  <div className="py-1.5">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">Description</dt>
                    <dd className="text-slate-700 text-[13px] leading-tight break-words"><DescriptionWithOrderLink t={t} onOpenOrder={onOpenOrder} /></dd>
                  </div>
                )}
                {t.paypal_order_id && t.payment_status && (
                  <div className="flex justify-between py-1.5 gap-3 items-center">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">PayPal</dt>
                    <dd>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        t.payment_status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700' :
                        t.payment_status === 'REFUND' || t.payment_status === 'REVERSAL' ? 'bg-amber-100 text-amber-800' :
                        t.payment_status === 'DISPUTED' ? 'bg-rose-200 text-rose-900' :
                        t.payment_status === 'FAILED' ? 'bg-red-100 text-red-700' :
                        t.payment_status === 'PENDING' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{t.payment_status}</span>
                    </dd>
                  </div>
                )}
              </dl>
              {(canInvoice || t.paypal_order_id || (t.type === 'admin_adjustment' && (t.manual_kind === 'credit' || t.manual_kind === 'debit' || !!t.is_bonus))) && (
                <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-2">
                  {canInvoice && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markTransactionSeen(t.id); downloadInvoice(t); }}
                      data-testid={`admin-invoice-mob-${t.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                    >
                      <FileText className="w-3.5 h-3.5" /> Invoice
                    </button>
                  )}
                  {t.paypal_order_id && (
                    <span onClick={(e) => e.stopPropagation()}>
                      <SyncPaypalButton txId={t.id} orderId={t.paypal_order_id} currentStatus={t.payment_status} token={token} onDone={load} />
                    </span>
                  )}
                  {t.type === 'admin_adjustment' && (t.manual_kind === 'credit' || t.manual_kind === 'debit' || !!t.is_bonus) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteAdjustment(t); }}
                      data-testid={`admin-tx-delete-mob-${t.id}`}
                      className="px-3 py-2 rounded-lg text-xs font-bold bg-red-50 text-red-700 hover:bg-red-100"
                      title="Delete adjustment"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {analytics && analytics.total_count > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <Pagination page={page} total={analytics.total_count} pageSize={PAGE_SIZE} onChange={setPage} label="transactions" isAdmin />
          </div>
        )}
      </div>
      {openCustomerId && (
        <CustomerProfileDrawer
          token={token}
          customerId={openCustomerId}
          onClose={() => setOpenCustomerId(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}


/**
 * Manual sync button — pings PayPal /v2/checkout/orders/{id} and refreshes
 * payment_status + paypal_capture_id on the transaction.
 * Useful when a webhook is missed or arrives late.
 */
function SyncPaypalButton({ txId, orderId, currentStatus, token, onDone }) {
  const [busy, setBusy] = useState(false);

  const sync = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paypal/sync-order/${orderId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert(`Sync failed: ${j.detail || `HTTP ${r.status}`}`);
        return;
      }
      alert(`Synced. PayPal status: ${j.status || 'unknown'}${j.capture_id ? `\nCapture: ${j.capture_id}` : ''}`);
      onDone && onDone();
    } catch (err) {
      alert(`Sync error: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={sync}
      disabled={busy}
      title={`Sync from PayPal (current: ${currentStatus || 'unknown'})`}
      data-testid={`tx-sync-paypal-${txId}`}
      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded inline-flex items-center justify-center disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
    </button>
  );
}
